import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook that delays showing loading states to prevent jarring flashes
 * during quick operations like thread switching
 *
 * @param {boolean} isLoading - The actual loading state
 * @param {number} delay - Delay in milliseconds before showing loading (default: 250ms)
 * @returns {boolean} - Whether to show the loading UI
 */
export function useDelayedLoading(isLoading, delay = 150) {
  const [showLoading, setShowLoading] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isLoading) {
      // Set a timeout to show loading after the delay
      timeoutRef.current = setTimeout(() => {
        setShowLoading(true);
        timeoutRef.current = null;
      }, delay);
    } else {
      // Loading finished - immediately hide loading
      setShowLoading(false);
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, delay]);

  // Also hide loading immediately when component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return showLoading;
}
